import {AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, ViewChild} from "@angular/core";
import {DialogRef} from "@fundamental-ngx/core/dialog";
import {UntypedFormBuilder as FormBuilder, Validators} from "@angular/forms";
import {firstValueFrom, Subscription} from "rxjs";
import {AutomationModel} from "../../../../core/models/misc/automation.model";
import {
    AutomationType, OperationalMode, OperationalScenario,
    WizardUtils
} from "../shared/WizardHelpers";
import {AutomationService} from "../../../../service/services/rest/automation/automation.service";
import {
    STEP_SETUP_FORM_CONTROLS,
    STEP_SETUP_FORM_GROUPS
} from "./unified-step-setup/unified-step-setup.component";
import {
    STEP_BEGIN_FORM_GROUPS
} from "./unified-step-begin/unified-step-begin.component";
import {Const, ResourceType} from "../../../../core/helpers/Const";
import {Utils} from "../../../../core/helpers/Utils";
import {CustomValidators} from "../../../../core/helpers/CustomValidators";
import {NestedValuePipe} from "../../../../shared/pipes/nested-value/nested-value.pipe";
import {WizardSideDialogComponent} from "../../../../shared/components/wizard-side-dialog/wizard-side-dialog.component";
import {ZipFile, ZipService} from "../../../../service/services/misc/zip/zip.service";
import {dump, load, loadAll} from "js-yaml";
import {MenuComponent} from "@fundamental-ngx/core";

export const PAYLOAD_FORM_GROUPS = {
    PAYLOAD: "payload",
    RESOURCE_TYPE_DEFINITION: "resourceTypeDefinition",
    SERVICE_METADATA: "serviceMetadata",
    SERVICE_PUBLISH_CONFIGURATION: "servicePublishConfiguration",
    AUTOMATION_SOLUTION: "automationSolution",
    SPFI_APPLICATION: "spfiApplication",
    MANAGED_APPLICATION_PROVIDER: "managedApplicationProvider",
    INTEGRATIONS: "integrations",
    CLD_TENANT_SYNC_ENABLEMENT: "cldTenantSyncEnablement",
    METERING_ACCOUNT: "account"
};

export const PAYLOAD_FORM_CONTROLS = {
    BASE_FOLDER_NAME: "baseFolderName",
    BASE_FOLDER_DISPLAY_NAME: "baseFolderDisplayName",
    MANAGED_SERVICE_NAME: "managedServiceName",
    RESOURCE_GROUP_NAME: "resourceGroupName",
    TEST_FOLDER_NAME: "testFolderName",
    TEST_RESOURCE_GROUP_NAME: "testResourceGroupName",
    RESOURCE_TYPE_DEFINITION: {
        NAME: "name",
        GROUP: "group",
        VERSION: "version",
        TYPE: "type",
        PLURAL: "plural",
        SINGULAR: "singular"
    },
    SERVICE_METADATA: {
        NAME: "name",
        APP_DISPLAY_NAME: "appDisplayName",
        APP_TECHNICAL_NAME: "appTechnicalName",
        APP_DESCRIPTION: "appDescription",
        CLD_SYSTEM_ROLE: "cldSystemRole",
        TENANT_PRODUCT_TYPE: "tenantProductType",
        LOCAL_TENANT_TYPE: "localTenantType",
        SERVICE: "service"
    },
    SERVICE_PUBLISH_CONFIGURATION: {
        NAME: "name"
    },
    AUTOMATION_SOLUTION: {
        NAME: "name",
        DISPLAY_NAME: "displayName",
        DESCRIPTION: "description",
        SUPPORT_INFO: "supportInfo"
    },
    SPFI_APPLICATION: {
        NAME: "name",
        ENDPOINT: "endpoint",
        CLIENT_ID: "clientId",
        ISSUER_NAME: "issuerName"
    },
    MANAGED_APPLICATION_PROVIDER: {
        NAME: "name",
        APPLICATION_TYPE: "applicationType",
        APPLICATION_NAMESPACE: "applicationNamespace",
        SUPPORT_COMPONENT: "supportComponent"
    },
    INTEGRATIONS: {
        NAME: "name"
    },
    CLD_TENANT_SYNC_ENABLEMENT: {
        NAME: "name"
    },
    METERING_ACCOUNT: {
        NAME: "name",
        LOCAL_TENANT_TYPE: "localTenantType"
    }
};

export enum ExecutionType {
    Create = 'create',
    CreateAndDownload = 'createAndDownload',
    Download = 'download'
}

@Component({
    selector: "app-unified-create-application-side-dialog",
    templateUrl: "./unified-create-application-dialog.component.html",
    styleUrls: ["./unified-create-application-dialog.component.scss"],
    standalone: false
})
export class UnifiedCreateApplicationDialogComponent implements AfterViewChecked, OnDestroy {
    readonly Const = Const;
    readonly ExecutionType = ExecutionType;
    readonly STEP_SETUP_FORM_GROUPS = STEP_SETUP_FORM_GROUPS;
    readonly STEP_BEGIN_FORM_GROUPS = STEP_BEGIN_FORM_GROUPS;
    @ViewChild('dialog') private dialog: WizardSideDialogComponent;
    readonly PAYLOAD_FORM_GROUPS = PAYLOAD_FORM_GROUPS;
    @ViewChild('menu') menu: MenuComponent;

    public loading = false;
    errorText: string;
    errorMsg: string;
    subscriptions: Subscription[] = [];

    executionTypes = [
        {
            key: ExecutionType.Create,
            text: "Set Up App",
            tooltip: "Generates the relevant the unified services framework resources in your folder"
        },
        {
            key: ExecutionType.CreateAndDownload,
            text: "Set Up App and Download Helm Chart",
            tooltip: "Generates the relevant the unified services framework resources in your folder, and downloads the resource files as a Helm chart"
        },
        {
            key: ExecutionType.Download,
            text: "Download Helm Chart",
            tooltip: "Downloads the resource files as a Helm chart (relevant resources are not yet created in the unified services framework)"
        }
    ];
    form = this._fb.group({});

    constructor(
        public dialogRef: DialogRef,
        private _fb: FormBuilder,
        private _cdRef: ChangeDetectorRef,
        private _automationService: AutomationService,
        private _nestedValuePipe: NestedValuePipe,
        private _zipService: ZipService
    ) {
        this.initPayloadControls();
    }

    ngAfterViewChecked() {
        this._cdRef.detectChanges();
    }

    onBeforeStepChange(stepIndex: number) {
        // If moving to the review step (step 2), set SUPPORT_COMPONENT default
        if (stepIndex === 2) {
            const mapGroup = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD)?.get(PAYLOAD_FORM_GROUPS.MANAGED_APPLICATION_PROVIDER);
            if (mapGroup) {
                const supportComponentControl = mapGroup.get(PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.SUPPORT_COMPONENT);
                if (supportComponentControl && !supportComponentControl.value) {
                    supportComponentControl.setValue("BC-CP-MP-SYS");
                }
            }
        }
    }

    initPayloadControls() {
        const rtdPluralControl = this.generatePropertyControl(ResourceType.RTD, "names.properties.plural", "myapptenants");
        const rtdPluralSubscription = rtdPluralControl.valueChanges.subscribe((val) => this.onRtdPluralChange(val));
        this.subscriptions.push(rtdPluralSubscription);
        this.form.addControl("payload", this._fb.group({
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_NAME]: ["", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_DISPLAY_NAME]: [""],
            [PAYLOAD_FORM_CONTROLS.MANAGED_SERVICE_NAME]: ["", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.RESOURCE_GROUP_NAME]: ["resources", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.TEST_FOLDER_NAME]: ["test", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.TEST_RESOURCE_GROUP_NAME]: ["test-resources", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_GROUPS.RESOURCE_TYPE_DEFINITION]: this._fb.group({
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.NAME]: ["", [Validators.required, Validators.maxLength(285), CustomValidators.ValidateTechnicalName]],
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.GROUP]: this.generatePropertyControl(ResourceType.RTD, "group", WizardUtils.generateGroup(this.dialogRef.data.path)),
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.VERSION]: this.generatePropertyControl(ResourceType.RTD, "version", "v1"),
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.TYPE]: this.generatePropertyControl(ResourceType.RTD, "names.properties.type", "MyAppTenant"),
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.PLURAL]: rtdPluralControl,
                [PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.SINGULAR]: this.generatePropertyControl(ResourceType.RTD, "names.properties.singular", "myapptenant")
            }),
            [PAYLOAD_FORM_GROUPS.SERVICE_METADATA]: this._fb.group({
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.NAME]: ["", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.APP_DISPLAY_NAME]: this.generatePropertyControl(ResourceType.SERVICE_METADATA, "products.items.properties.displayName", "My App Display Name"),
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.APP_TECHNICAL_NAME]: this.generatePropertyControl(ResourceType.SERVICE_METADATA, "products.items.properties.technicalName", "my-app"),
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.APP_DESCRIPTION]: this.generatePropertyControl(ResourceType.SERVICE_METADATA, "products.items.properties.description", "My app's description"),
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.CLD_SYSTEM_ROLE]: this.generatePropertyControl(ResourceType.SERVICE_METADATA, "commercialMappingConfigurations.items.properties.cldSystemRole", "", false),
                [PAYLOAD_FORM_CONTROLS.SERVICE_METADATA.TENANT_PRODUCT_TYPE]: this.generatePropertyControl(ResourceType.SERVICE_METADATA, "commercialMappingConfigurations.items.properties.tenantProductType", "", false),
            }),
            [PAYLOAD_FORM_GROUPS.SERVICE_PUBLISH_CONFIGURATION]: this._fb.group({
                [PAYLOAD_FORM_CONTROLS.SERVICE_PUBLISH_CONFIGURATION.NAME]: ["", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]]
            })
        }));
    }

    onRtdPluralChange(val: string) {
        const rtdNameControl = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD)?.get(PAYLOAD_FORM_GROUPS.RESOURCE_TYPE_DEFINITION)?.get(PAYLOAD_FORM_CONTROLS.RESOURCE_TYPE_DEFINITION.NAME);
        const appDisplayName = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME).value;
        const technicalName = WizardUtils.convertToTechnicalName(appDisplayName, "");
        const newPath = `${this.dialogRef.data.path}/${technicalName}`;
        const updatedGroup = WizardUtils.generateGroup(newPath);
        rtdNameControl?.setValue(`${val}.${updatedGroup}`);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    validateForm() {
        return this.form.valid && !this.loading;
    }

    async onSubmit(executionType: ExecutionType) {
        if (this.menu) {
            this.menu.close();
        }

        // Set SUPPORT_COMPONENT to "BC-CP-MP-SYS" if it's empty
        const mapGroup = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD)?.get(PAYLOAD_FORM_GROUPS.MANAGED_APPLICATION_PROVIDER);
        if (mapGroup) {
            const supportComponentControl = mapGroup.get(PAYLOAD_FORM_CONTROLS.MANAGED_APPLICATION_PROVIDER.SUPPORT_COMPONENT);
            if (supportComponentControl && !supportComponentControl.value) {
                supportComponentControl.setValue(Const.DEFAULT_SUPPORT_COMPONENT);
            }
        }

        this.setLoading(true, executionType);

        let automationParams: AutomationModel;
        try {
            automationParams = await this.generateParams(executionType);
            const query = "?unifiedWizard=true";
            const res = await firstValueFrom(this._automationService.runAutomation(automationParams, query));
            const returnParams = this.generateReturnParams(res, executionType);
            //Check if HELM result should be downloaded
            if (executionType !== ExecutionType.Create && res.helmResult) {
                await this.downloadHelmTemplate(res.helmResult, returnParams.payload[PAYLOAD_FORM_CONTROLS.MANAGED_SERVICE_NAME]);
            }
            this.dialogRef.close(returnParams);
            this.setLoading(false, executionType);
        } catch (e: any) {
            this.errorText = this.getErrorText(executionType);
            this.errorMsg = e.message;
            if (this.errorMsg?.includes("manifests contain a resource that already exists")) {
                if (this.errorMsg.includes("helm-plugin.resource.api.sap/release-name")) {
                    this.errorText = $localize`:XMSG|Unified wizard app already exist error@@organizations.unified-create-application-dialog.alreadyExistError:Cannot set up app due to existing resources in the folder`;
                    this.errorMsg = $localize`:XMSG|Unified wizard app already exist error msg@@organizations.unified-create-application-dialog.alreadyExistErrorMsg:Folder ${automationParams.parameters.baseFolderName} in ${automationParams.parameters.path} contains existing resources that prevent setting up your app. Please close the wizard, manually delete any resources in the folder, and then rerun the wizard.`;
                } else {
                    this.errorText = $localize`:XMSG|Generic resource exists error@@organizations.unified-create-application-dialog.genericResourceExistsError:Cannot set up app due to an error.`;
                }
            }
            this.setLoading(false, executionType);
            this._cdRef.detectChanges();
            this.dialog.focusErrorStrip();
        }
    }

    generateReturnParams(automationResult: any, executionType: ExecutionType) {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD).value;
        const stepSetupFields = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP);
        const operationalData = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_GROUPS.OPERATIONAL_DATA);
        const provisioningData = stepSetupFields.get(STEP_SETUP_FORM_GROUPS.PROVISIONING);
        const operationalMode = operationalData.get(STEP_SETUP_FORM_CONTROLS.OPERATIONAL_MODE).value;
        const isBTPApplication = provisioningData.get(STEP_SETUP_FORM_CONTROLS.IS_BTP_APPLICATION)?.value;
        const operationalScenario = provisioningData?.get(STEP_SETUP_FORM_CONTROLS.SCENARIO)?.value;
        const subaccount = provisioningData.get(STEP_SETUP_FORM_CONTROLS.SUBACCOUNT)?.value;
        const templateSubaccount = provisioningData.get(STEP_SETUP_FORM_CONTROLS.TEMPLATE_SUBACCOUNT)?.value;
        const appDisplayName = stepSetupFields.get(STEP_SETUP_FORM_CONTROLS.APP_DISPLAY_NAME)?.value;
        return {
            res: automationResult,
            payload: payload,
            operationalMode: operationalMode,
            isBTPApplication: isBTPApplication,
            operationalScenario: operationalScenario,
            subaccount: subaccount,
            templateSubaccount: templateSubaccount,
            appDisplayName: appDisplayName,
            executionType: executionType
        };
    }

    async generateParams(executionType: ExecutionType) {
        const step1fields = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).value;
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD).value;
        const operationalData = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_GROUPS.OPERATIONAL_DATA);
        const provisioningData = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_GROUPS.PROVISIONING);
        const operationalMode = operationalData.get(STEP_SETUP_FORM_CONTROLS.OPERATIONAL_MODE)?.value;
        const operationalScenario = provisioningData?.get(STEP_SETUP_FORM_CONTROLS.SCENARIO)?.value;
        const productMetadata = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).get(STEP_SETUP_FORM_GROUPS.PRODUCT_METADATA);
        const serviceId = productMetadata?.value?.service?.key;
        const isOfferedInBtp = provisioningData?.get(STEP_SETUP_FORM_CONTROLS.IS_BTP_APPLICATION)?.value === "yes";

        if (serviceId) {
            payload.serviceMetadata.serviceId = serviceId;
        }

        if(operationalMode === OperationalMode.SAP && operationalScenario === OperationalScenario.UCA_LEASED && !isOfferedInBtp) {
            // load sap-managed/rest-solution.yaml and add main.tf and variables.tf to the payload
            const restSolutionYamlPath = 'assets/cloud-automation-designer/templates/solutions/sap-managed/rest-solution.yaml';
            try {
                const response = await fetch(restSolutionYamlPath);
                if (!response.ok) {
                    throw new Error(`Failed to load rest solution template: ${response.status} ${response.statusText}`);
                }
                const yamlContent = await response.text();
                const parsedYaml: any = load(yamlContent);

                if (parsedYaml.content) {
                    payload.mainTf = parsedYaml.content['main.tf'];
                    payload.variablesTf = parsedYaml.content['variables.tf'];
                }
            } catch (error: any) {
                throw new Error(`Failed to load rest solution template: ${error.message || error}`);
            }
        }

        let automationParams: AutomationModel = {
            automationId: AutomationType.BTP_APPLICATION,
            parameters: {
                executionType: this.mapExecutionType(executionType),
                path: this.dialogRef.data.path,
                landscape: step1fields.provisioning.landscape,
                operationalScenario: step1fields.provisioning.scenario,
                operationalMode: operationalMode,
                ...payload
            }
        };

        // Only add BTP-related fields if they have values
        if (step1fields.provisioning.globalAccount) {
            automationParams.parameters.globalAccount = step1fields.provisioning.globalAccount;
        }
        if (step1fields.provisioning.subaccount) {
            automationParams.parameters.subaccount = step1fields.provisioning.subaccount;
        }
        if (step1fields.provisioning.templateSubaccount) {
            automationParams.parameters.templateSubaccount = step1fields.provisioning.templateSubaccount;
        }
        return automationParams;
    }

    setLoading(isLoading: boolean, executionType: ExecutionType) {
        this.loading = isLoading;
        let html;
        let busyText;
        if (executionType === ExecutionType.Download) {
            html = `
              <div>
                <p>We're currently creating the Helm chart for your app and the required resources in the unified services framework.</p>
                <p><strong>Please do not close or refresh this browser page until the process has finished.</strong></p>
              </div>`;
            busyText = $localize`:YMSG|Unified wizard loading text download@@organizations.unified-create-application-dialog.loadingTextDownload:${html}`;
        } else {
            html = `
              <div>
                <p>We're currently setting up your app and the required resources in the unified services framework.</p>
                <p>This process can take up to 2-3 minutes to complete.</p>
                <p><strong>Please do not close or refresh this browser page until the process has finished.</strong></p>
              </div>`;
            busyText = $localize`:YMSG|Unified wizard loading text@@organizations.unified-create-application-dialog.loadingText:${html}`;
        }
        this.dialog.setBusy(isLoading, busyText);
    }

    getErrorText(executionType: ExecutionType) {
        const defaultError = "Cannot create app";
        switch (executionType) {
        case ExecutionType.Create:
            return defaultError;
        case ExecutionType.CreateAndDownload:
            return "Cannot create app and download the Helm chart";
        case ExecutionType.Download:
            return "Cannot create and download Helm chart";
        default:
            return defaultError;
        }
    }

    generatePropertyControl(resourceType: ResourceType, propertyPath: string, defaultValue: string, required = true) {
        const dialogData = this.dialogRef.data;
        const r = dialogData?.applicationResources.find((r: any) => r.type === resourceType);
        const spec = r?.versions?.[0].schema.jsonSchema.properties.spec.properties;
        const prop = this._nestedValuePipe.transform(spec, propertyPath);
        const control = Utils.getFormControl(prop, defaultValue, required);
        return control;
    }

    async downloadHelmTemplate(helmResult: any, managedServiceName: string) {
        let content: ZipFile[] = [];
        Object.keys(helmResult.manifests).forEach((key) => {
            const data = loadAll(helmResult.manifests[key]);
            const yaml: any = data.map(doc => dump(doc)).join('---\n');
            content.push(new ZipFile(`templates/${key}`, yaml));
        });

        const chartYaml = load(helmResult.chart);
        content.push(new ZipFile("Chart.yaml", dump(chartYaml)));
        let valuesYaml = "";

        if (helmResult.values) {
            valuesYaml = dump(helmResult.values);
        }
        content.push(new ZipFile("values.yaml", valuesYaml));

        await this._zipService.generateZip(content, `${managedServiceName}-helm.zip`);
    }

    mapExecutionType(executionType: ExecutionType) {
        switch (executionType) {
        case ExecutionType.Create:
        case ExecutionType.CreateAndDownload:
            return "install";
        default:
            return "render";
        }
    }
}
